import React, { useState, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Badge,
  Tooltip,
  Collapse,
  useTheme,
  useMediaQuery,
  alpha,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  AccountTree as AccountTreeIcon,
  AccountBalanceWallet as WalletIcon,
  Assignment as ProjectIcon,
  Business as BusinessIcon,
  BarChart as ReportIcon,
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  SwapHoriz as SwapIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  ExpandLess,
  ExpandMore,
  Groups as GroupsIcon,
  Apartment as ApartmentIcon,
  Public as PublicIcon,
  Store as StoreIcon,
  LocalAtm as LocalAtmIcon,
  Insights as InsightsIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SupervisorIcon,
  BusinessCenter as BusinessCenterIcon,
  Hub as HubIcon,
  AccountBalance as AccountBalanceIcon,
  ReceiptLong as ReceiptLongIcon,
  Storefront as StorefrontIcon,
  Inventory2 as Inventory2Icon,
  Token as TokenIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import CoopNetLogo from '../components/CoopNetLogo';
import SelectorVistaDialog from '../components/SelectorVistaDialog';

const drawerWidth = 280;

// =============================================
// CONFIGURACIÓN DE MENÚ POR NIVEL
// =============================================

const getMenuByLevel = (nivel, contexto) => {
  const baseMenuAfiliado = [
    { 
      title: 'Mi Dashboard', 
      path: '/dashboard', 
      icon: <DashboardIcon />,
      description: 'Resumen personal',
    },

    { 
      title: 'Mi Wallet', 
      path: '/wallet', 
      icon: <WalletIcon />,
      description: 'Saldo y movimientos',
    },
    { 
      title: 'Mis Proyectos', 
      path: '/proyectos', 
      icon: <ProjectIcon />,
      description: 'Estado de los proyectos de tu cooperativa',
    },
  ];

  const menuCooperativa = [
    { 
      title: 'Dashboard', 
      path: '/dashboard', 
      icon: <DashboardIcon />,
      description: 'Panel de la cooperativa',
    },
    { 
      section: 'Gestión',
      items: [
        { 
          title: 'Afiliados', 
          path: '/afiliados', 
          icon: <PeopleIcon />,
          description: 'Gestión de los 20 miembros',
        },
        { 
          title: 'Comités', 
          path: '/comites', 
          icon: <GroupsIcon />,
          description: '5 comités principales',
        },
        { 
          title: 'Proyectos', 
          path: '/proyectos', 
          icon: <ProjectIcon />,
          description: 'Radicar y tramitar proyectos',
        },
      ]
    },
    { 
      section: 'Finanzas',
      items: [
        { 
          title: 'Fondo Solidario', 
          path: '/wallet', 
          icon: <LocalAtmIcon />,
          description: 'Capital cooperativo',
        },
        { 
          title: 'Aportes y Crédito', 
          path: '/aportes-credito', 
          icon: <AccountBalanceIcon />,
          description: 'Aportes sociales y crédito de consumo',
        },
        { 
          title: 'Contabilidad', 
          path: '/contabilidad', 
          icon: <ReceiptLongIcon />,
          description: 'Comprobantes, libros, terceros y reportes',
        },

      ]
    },
    { 
      section: 'Operaciones',
      items: [
        { 
          title: 'Nómina', 
          path: '/nomina', 
          icon: <GroupsIcon />,
          description: 'Empleados, causación y pago',
        },
        { 
          title: 'Compras y Proveedores', 
          path: '/compras', 
          icon: <StorefrontIcon />,
          description: 'Causación con retención y pagos',
        },
        { 
          title: 'Activos Fijos', 
          path: '/activos-fijos', 
          icon: <Inventory2Icon />,
          description: 'Registro y depreciación',
        },
        { 
          title: 'Financiamiento de Proyectos', 
          path: '/financiamiento-proyectos', 
          icon: <AccountBalanceIcon />,
          description: 'Banco de Proyectos - Art. 62-64',
        },
      ]
    },
    { 
      section: 'Administración',
      items: [
        { 
          title: 'Reportes', 
          path: '/reportes', 
          icon: <ReportIcon />,
          description: 'Informes consolidados',
        },
        { 
          title: 'Configuración', 
          path: '/configuracion', 
          icon: <SettingsIcon />,
          description: 'Ajustes del sistema',
        },
        { 
          title: 'Auditoría', 
          path: '/auditoria', 
          icon: <HistoryIcon />,
          description: 'Quién cambió cada cosa',
        },
      ]
    },
  ];

  const menuFederacion = [
    { 
      title: 'Dashboard', 
      path: '/dashboard', 
      icon: <DashboardIcon />,
      description: `Panel de ${contexto?.federacion?.nombre || 'Federación'}`,
    },
    { 
      section: 'Red de Cooperativas',
      items: [
        { 
          title: 'Cooperativas', 
          path: '/cooperativas', 
          icon: <BusinessIcon />,
          description: 'Cooperativas afiliadas',
        },
        { 
          title: 'Afiliados', 
          path: '/afiliados', 
          icon: <PeopleIcon />,
          description: 'Todos los afiliados',
        },
        { 
          title: 'Proyectos', 
          path: '/proyectos', 
          icon: <ProjectIcon />,
          description: 'Decidir los proyectos remitidos por tus cooperativas',
        },
        { 
          title: 'Comités', 
          path: '/comites', 
          icon: <GroupsIcon />,
          description: 'Comités de dirección',
        },
      ]
    },
    { 
      section: 'Finanzas',
      items: [
        { 
          title: 'Capital Federado', 
          path: '/wallet', 
          icon: <LocalAtmIcon />,
          description: 'Consolidado financiero',
        },

      ]
    },
    { 
      section: 'Administración',
      items: [
        { 
          title: 'Reportes', 
          path: '/reportes', 
          icon: <ReportIcon />,
          description: 'Informes federación',
        },
        { 
          title: 'Configuración', 
          path: '/configuracion', 
          icon: <SettingsIcon />,
          description: 'Configuración federación',
        },
        { 
          title: 'Auditoría', 
          path: '/auditoria', 
          icon: <HistoryIcon />,
          description: 'Quién cambió cada cosa',
        },
      ]
    },
  ];

  const menuConfederacion = [
    { 
      title: 'Dashboard Global', 
      path: '/dashboard', 
      icon: <DashboardIcon />,
      description: 'Visión consolidada',
    },
    {
      title: 'Administración de la Red',
      path: '/administracion',
      icon: <AdminIcon />,
      description: 'Estructura y roles (datos reales)',
    },
    { 
      section: 'Estructura Jerárquica',
      items: [
        { 
          title: 'Confederaciones', 
          path: '/confederaciones', 
          icon: <PublicIcon />,
          description: 'Red internacional',
        },
        { 
          title: 'Federaciones', 
          path: '/federaciones', 
          icon: <ApartmentIcon />,
          description: 'Federaciones por país',
        },
        { 
          title: 'Cooperativas', 
          path: '/cooperativas', 
          icon: <BusinessIcon />,
          description: 'Todas las cooperativas',
        },
        { 
          title: 'Afiliados', 
          path: '/afiliados', 
          icon: <PeopleIcon />,
          description: 'Base total de afiliados',
        },
        { 
          title: 'Comités', 
          path: '/comites', 
          icon: <GroupsIcon />,
          description: 'Comités internacionales',
        },
      ]
    },
    { 
      section: 'Operaciones',
      items: [
        { 
          title: 'Proyectos Globales', 
          path: '/proyectos', 
          icon: <ProjectIcon />,
          description: 'Banco de Proyectos de toda la red',
        },
        { 
          title: 'Capital Total', 
          path: '/wallet', 
          icon: <LocalAtmIcon />,
          description: 'Finanzas consolidadas',
        },

      ]
    },
    { 
      section: 'Administración Central',
      items: [
        { 
          title: 'Reportes Ejecutivos', 
          path: '/reportes', 
          icon: <InsightsIcon />,
          description: 'Análisis macro',
        },
        { 
          title: 'Configuración Global', 
          path: '/configuracion', 
          icon: <AdminIcon />,
          description: 'Parámetros del sistema',
        },
        { 
          title: 'Auditoría', 
          path: '/auditoria', 
          icon: <HistoryIcon />,
          description: 'Quién cambió cada cosa en toda la red',
        },
      ]
    },
  ];

  switch (nivel) {
    case 'confederacion':
      return menuConfederacion;
    case 'federacion':
      return menuFederacion;
    case 'cooperativa':
      return menuCooperativa;
    case 'afiliado':
    default:
      return baseMenuAfiliado;
  }
};

// =============================================
// COMPONENTE PRINCIPAL
// =============================================

const MainLayout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, contexto, getNivelLabel, getNivelColor, vistas, setSelectorAbierto, resumenRed } = useAuth();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [openSections, setOpenSections] = useState({});

  // Obtener menú según nivel del usuario
  const menuItems = useMemo(() => {
    const base = getMenuByLevel(user?.nivel, contexto);
    // Contenido exclusivo de SuperAdmin, visible en cualquier vista que esté usando.
    if (!user?.esSuperAdmin) return base;
    return [
      ...base,
      {
        section: 'SuperAdmin',
        items: [
          {
            title: 'NFTs y Blockchain',
            path: '/nft-cooperativas',
            icon: <TokenIcon />,
            description: 'Presentación para cooperativas',
          },
        ],
      },
    ];
  }, [user?.nivel, user?.esSuperAdmin, contexto]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const handleSectionToggle = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // CoopNet aún no tiene servicio de notificaciones. Antes aquí se mostraban las de
  // ejemplo (inventadas); se deja vacío hasta que exista una fuente real.
  const notificaciones = [];
  const unreadNotifications = 0;

  // Obtener información del contexto actual
  const getContextInfo = () => {
    if (!user) return null;
    const cuenta = (v, uno, varios) => `${v ?? 0} ${v === 1 ? uno : varios}`;

    // Todas las cifras salen de GET organizacion/resumen (datos reales).
    switch (user.nivel) {
      case 'confederacion':
        return {
          label: 'Confederación Internacional',
          sublabel: cuenta(resumenRed?.totales?.paises, 'país', 'países'),
        };
      case 'federacion': {
        const n = resumenRed?.federaciones?.find((f) => f.id === contexto?.federacion?.id);
        return {
          label: contexto?.federacion?.nombre || 'Federación',
          sublabel: cuenta(n?.cooperativas, 'cooperativa', 'cooperativas'),
        };
      }
      case 'cooperativa': {
        const n = resumenRed?.cooperativas?.find((k) => k.id === contexto?.cooperativa?.id);
        return {
          label: contexto?.cooperativa?.nombre || 'Cooperativa',
          sublabel: cuenta(n?.afiliados, 'afiliado', 'afiliados'),
        };
      }
      case 'afiliado':
        return {
          label: contexto?.cooperativa?.nombre || 'Mi Cooperativa',
          sublabel: contexto?.afiliado?.estado ? `Afiliado ${contexto.afiliado.estado}` : 'Afiliado',
        };
      default:
        return null;
    }
  };

  const contextInfo = getContextInfo();
  const nivelColor = getNivelColor(user?.nivel);

  // =============================================
  // SIDEBAR DRAWER
  // =============================================

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header del Sidebar */}
      <Box
        sx={{
          p: 3,
          background: `linear-gradient(135deg, ${nivelColor} 0%, ${alpha(nivelColor, 0.8)} 100%)`,
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: '1.2rem',
            }}
          >
            <CoopNetLogo size={40} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              CoopNet
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              Red Cooperativa
            </Typography>
          </Box>
        </Box>
        
        {/* Chip de nivel */}
        <Chip
          size="small"
          label={getNivelLabel(user?.nivel)}
          sx={{
            bgcolor: 'rgba(255,255,255,0.2)',
            color: 'white',
            fontWeight: 600,
            '& .MuiChip-label': { px: 2 },
          }}
        />
      </Box>

      {/* Info del contexto */}
      {contextInfo && (
        <Box
          sx={{
            px: 3,
            py: 2,
            bgcolor: alpha(nivelColor, 0.05),
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: nivelColor }}>
            {contextInfo.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {contextInfo.sublabel}
          </Typography>
        </Box>
      )}

      {/* Lista de navegación */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        <List component="nav" disablePadding>
          {menuItems.map((item, index) => {
            // Item simple (sin sección)
            if (item.path) {
              const isActive = location.pathname === item.path;
              return (
                <ListItemButton
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    mx: 1.5,
                    my: 0.5,
                    borderRadius: 2,
                    bgcolor: isActive ? alpha(nivelColor, 0.1) : 'transparent',
                    color: isActive ? nivelColor : 'text.primary',
                    '&:hover': {
                      bgcolor: alpha(nivelColor, 0.08),
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 40,
                      color: isActive ? nivelColor : 'text.secondary',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    secondary={item.description}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 600 : 500,
                      fontSize: '0.9rem',
                    }}
                    secondaryTypographyProps={{
                      fontSize: '0.75rem',
                      sx: { mt: 0.25 },
                    }}
                  />
                </ListItemButton>
              );
            }

            // Sección con items
            if (item.section) {
              const isOpen = openSections[item.section] !== false;
              return (
                <React.Fragment key={item.section}>
                  <ListItemButton
                    onClick={() => handleSectionToggle(item.section)}
                    sx={{
                      mx: 1.5,
                      mt: index > 0 ? 1.5 : 0.5,
                      mb: 0.5,
                      borderRadius: 2,
                      bgcolor: alpha(theme.palette.grey[500], 0.05),
                    }}
                  >
                    <ListItemText
                      primary={item.section}
                      primaryTypographyProps={{
                        fontWeight: 700,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        color: 'text.secondary',
                      }}
                    />
                    {isOpen ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  
                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {item.items.map((subItem) => {
                        const isActive = location.pathname === subItem.path;
                        return (
                          <ListItemButton
                            key={subItem.path}
                            onClick={() => handleNavigation(subItem.path)}
                            sx={{
                              mx: 1.5,
                              my: 0.25,
                              borderRadius: 2,
                              bgcolor: isActive ? alpha(nivelColor, 0.1) : 'transparent',
                              color: isActive ? nivelColor : 'text.primary',
                              '&:hover': {
                                bgcolor: alpha(nivelColor, 0.08),
                              },
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: 36,
                                color: isActive ? nivelColor : 'text.secondary',
                              }}
                            >
                              {subItem.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={subItem.title}
                              secondary={subItem.description}
                              primaryTypographyProps={{
                                fontWeight: isActive ? 600 : 500,
                                fontSize: '0.875rem',
                              }}
                              secondaryTypographyProps={{
                                fontSize: '0.7rem',
                              }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                </React.Fragment>
              );
            }

            return null;
          })}
        </List>
      </Box>

      {/* Footer del Sidebar - Usuario */}
      <Box
        sx={{
          p: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          bgcolor: alpha(theme.palette.grey[500], 0.03),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              bgcolor: nivelColor,
              fontWeight: 600,
            }}
          >
            {user?.nombre?.[0]}{user?.apellido?.[0]}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, lineHeight: 1.2 }}
              noWrap
            >
              {user?.nombreCompleto || user?.nombre}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ display: 'block' }}
            >
              {user?.rolInfo?.nombre || user?.rol}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );

  // =============================================
  // RENDER
  // =============================================

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' }, color: 'text.primary' }}
          >
            <MenuIcon />
          </IconButton>

          {/* Título dinámico según contexto */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600 }}>
              {contextInfo?.label || 'CoopNet'}
            </Typography>
          </Box>

          {/* Notificaciones */}
          <Tooltip title="Notificaciones">
            <IconButton
              onClick={(e) => setNotifAnchor(e.currentTarget)}
              sx={{ color: 'text.secondary' }}
            >
              <Badge badgeContent={unreadNotifications} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          {/* Avatar / Menú usuario */}
          <Tooltip title="Mi cuenta">
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 1 }}>
              <Avatar
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: nivelColor,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                }}
              >
                {user?.nombre?.[0]}{user?.apellido?.[0]}
              </Avatar>
            </IconButton>
          </Tooltip>

          {/* Menú de usuario */}
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: { width: 240, mt: 1 },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.nombreCompleto}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
              <Chip
                size="small"
                label={getNivelLabel(user?.nivel)}
                sx={{
                  mt: 1,
                  bgcolor: alpha(nivelColor, 0.1),
                  color: nivelColor,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            </Box>
            <Divider />
            {vistas.length > 1 && (
              <MenuItem onClick={() => { setAnchorEl(null); setSelectorAbierto(true); }}>
                <ListItemIcon><SwapIcon fontSize="small" /></ListItemIcon>
                Cambiar de vista
              </MenuItem>
            )}
            <MenuItem onClick={() => { setAnchorEl(null); navigate('/perfil'); }}>
              <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
              Mi Perfil
            </MenuItem>
            <MenuItem onClick={() => { setAnchorEl(null); navigate('/configuracion'); }}>
              <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
              Configuración
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
              <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: 'error.main' }} /></ListItemIcon>
              Cerrar Sesión
            </MenuItem>
          </Menu>

          {/* Menú de notificaciones */}
          <Menu
            anchorEl={notifAnchor}
            open={Boolean(notifAnchor)}
            onClose={() => setNotifAnchor(null)}
            PaperProps={{
              sx: { width: 360, maxHeight: 400, mt: 1 },
            }}
          >
            <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Notificaciones
              </Typography>
            </Box>
            {notificaciones?.slice(0, 5).map((notif) => (
              <MenuItem
                key={notif.id}
                onClick={() => setNotifAnchor(null)}
                sx={{
                  py: 1.5,
                  bgcolor: !notif.leida ? alpha(theme.palette.primary.main, 0.05) : 'transparent',
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: !notif.leida ? 600 : 400 }}>
                    {notif.titulo}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {notif.mensaje?.substring(0, 50)}...
                  </Typography>
                </Box>
              </MenuItem>
            ))}
            <Divider />
            <MenuItem
              onClick={() => { setNotifAnchor(null); navigate('/notificaciones'); }}
              sx={{ justifyContent: 'center', color: 'primary.main' }}
            >
              Ver todas las notificaciones
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>

        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: `1px solid ${theme.palette.divider}`,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Contenido Principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: alpha(theme.palette.grey[500], 0.03),
        }}
      >
        <Toolbar />
        <Box sx={{ p: 3 }}>
          <Outlet />
        </Box>
      </Box>
      <SelectorVistaDialog />
    </Box>
  );
};

export default MainLayout;
